import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogContentText,
  Typography,
  Button,
  useMediaQuery,
  Box,
  Chip as MUIChip,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAccount } from '../../contexts/Account';
import DefaultAdvisorAvatar from '../../icons/user-avatar.svg';
import DefaultCompany from '../../icons/building_round.svg';
import CalendarSyncMenu from '../../components/CalendarSyncMenu';
import ACSnackbar from '../../components/ACSnackbar';
import { SnackbarType } from '../../services/utils/types';
import categoryBenefitsMap from '../../utils/categoryBenefitsMap';

const useStyles = ({ isSM }) => ({
  imageWrapper: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
  },
  ownerImageWrapper: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    marginTop: '20px',
  },
  dialogCompanyImage: {
    width: '73px',
    height: '73px',
    maxHeight: '73px',
    maxWidth: '73px',
    borderRadius: '146px',
    marginBottom: isSM ? 0 : '4px',
    cursor: 'pointer',
  },
  dialogContent: {
    overflow: 'hidden',
    maxHeight: isSM ? null : '600px',
    marginTop: isSM ? '0px' : '-30px',
    padding: isSM ? '20px' : '20px 24px',
  },
  dialogOwnerImage: {
    width: '60px',
    height: '60px',
    maxHeight: '60px',
    maxWidth: '60px',
    borderRadius: '146px',
    marginRight: '20px',
    cursor: 'pointer',
  },
  dialogContainer: {
    padding: '10px',
  },
  dialogActions: {
    display: 'flex',
    flexDirection: isSM ? 'column' : 'row',
    rowGap: isSM ? '10px' : 'unset',
    justifyContent: 'center',
    marginBottom: isSM ? '-10px' : '',
    paddingBottom: isSM ? '20px' : '',
  },
  dialog: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    justifyContent: 'center',
    width: isSM ? '' : '600px',
    height: 'auto',
    alignItems: 'center',
    background: '#FFFFFF',
    borderRadius: '15px',
    paddingTop: isSM ? '0px' : '30px',
    paddingBottom: isSM ? '0px' : '30px',
  },
  dialogText: {
    textAlign: 'left',
    mx: isSM ? '25px' : '55px',
  },
  dialogTextCompanyName: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    maxWidth: '100%',
    // paddingBottom: '16px',
  },
  dialogTextCompanyNameContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  dialogBoardOwnerContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
});

export function FirstViewModalLegacy({ companyBoardData }) {
  const params = useParams('/:companySlug/boardrooms');
  const { companySlug } = params;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const featuredBenefits = companyBoardData.benefits
    ? companyBoardData.benefits.filter((benefit) => benefit.featured)
    : [];
  const {
    accountDetails,
    asLoggedUserDetails,
    isFreemium,
  } = useAccount();
  const [isFirstViewModalVisible, setIsFirstViewModalVisible] = useState(true);
  const [companyBoardOwner, setCompanyBoardOwner] = useState();

  const [googleSync, setGoogleSync] = useState(
    accountDetails?.calendarTokens?.google || false
  );
  const [appleSync, setAppleSync] = useState(
    accountDetails?.calendarTokens?.apple || false
  );
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState(SnackbarType.SUCCESS);

  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const classes = useStyles({ isSM });

  useEffect(() => {
    let isMounted = true;

    const fetchCompanyData = () => {
      try {
        if (isMounted && companyBoardData) {
          const companyContacts = companyBoardData.contacts || [];
          const companyOwner = companyContacts.find((c) => c.isBoardOwner);
          setCompanyBoardOwner(companyOwner || companyContacts[0]);
        }
      } catch (error) {
        console.error('Error fetching company data:', error);
      }
    };

    const initializeModal = () => {
      if (!companySlug) return;

      const selectedCompany =
        asLoggedUserDetails.advisorRelatedCompanies.memberBoards.find(
          (element) => element.slug === companySlug
        );

      if (selectedCompany?.id) {
        fetchCompanyData(selectedCompany.id);
      }
    };

    initializeModal();

    return () => {
      isMounted = false;
    };
  }, [companySlug, asLoggedUserDetails, queryParams]);

  function getTextTrimmed(text, maxSize) {
    if (!text) return '';
    if (text.length <= maxSize) {
      return text;
    }
    return text.substring(0, maxSize) + '...';
  }

  const allPills = [];
  Object.entries(categoryBenefitsMap).forEach(([cat, { icon, label }]) => {
    if (featuredBenefits?.some((b) => b.category === cat)) {
      allPills.push({ icon, label });
    }
  });
  if ((featuredBenefits?.length ?? 0) < 4) {
    allPills.push({ icon: '📝', label: 'Add to Resume' });
  }
  let displayPills = allPills;

  return (
    <>
      <Dialog
        open={isFirstViewModalVisible}
        sx={classes.dialogContainer}
        fullScreen={isSM}
        maxWidth="sm"
        PaperProps={{ style: { borderRadius: 15 } }}
        onClose={() => setIsFirstViewModalVisible(false)}
      >
        <div style={classes.dialog}>
          <DialogContent sx={classes.dialogContent}>
            {isSM && (
              <IconButton
                size="small"
                onClick={() => {
                  setIsFirstViewModalVisible(false);
                }}
                sx={{ position: 'absolute', top: 0, right: 0 }}
              >
                <CloseIcon />
              </IconButton>
            )}
            <div role="none" style={classes.imageWrapper} onKeyDown={() => {}}>
              <img
                alt=""
                className="profileAvatar"
                style={classes.dialogCompanyImage}
                src={
                  companyBoardData?.image?.id &&
                  !companyBoardData.image.id.includes('undefined')
                    ? `${process.env.REACT_APP_IMAGES_URL}${companyBoardData.image.id}`
                    : DefaultCompany
                }
              />
            </div>
            <div style={classes.dialogTextCompanyNameContainer}>
              <DialogContentText sx={classes.dialogTextCompanyName}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '16px',
                    lineHeight: '24px',
                    letterSpacing: '-0.43px',
                    textTransform: 'none',
                    fontWeight: 700,
                    color: '#232B35',
                    verticalAlign: 'middle',
                    marginTop: '8px',
                  }}
                >
                  {`${accountDetails.givenName}, welcome to our advisory board!`}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: '#232B35',
                    lineHeight: '20px',
                    letterSpacing: '-0.43px',
                    mt: '8px',
                    // marginBottom: '8px',
                  }}
                >
                  Here's how it works:
                </Typography>
              </DialogContentText>
            </div>
            <DialogContentText sx={classes.dialogText}>
              <ol
                style={{
                  listStyleType: 'decimal',
                  fontSize: 13,
                  paddingLeft: 20,
                  color: '#232B35',
                  margin: '10px 0',
                }}
              >
                <li style={{ marginBottom: 12 }}>
                  Every month you’ll receive a Board Member Update email that
                  makes it easy for you to share your ideas and stay up to date.
                </li>
                <li style={{ marginBottom: 12 }}>
                  If the update sparks an idea or advice you’d like to share,
                  we’d love to hear it and you can reply right from the email!
                </li>
                <li style={{ marginBottom: 12 }}>
                  Plus you can interact at any time in our digital boardroom,
                  and you’re welcome to attend quarterly board meetings if
                  available.
                </li>
              </ol>
            </DialogContentText>

            <Box sx={classes.dialogActions}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: 13,
                  fontWeight: 400,
                  color: '#232B35',
                  lineHeight: '20px',
                  letterSpacing: '-0.43px',
                  textAlign: 'center',
                  px: '25px',
                }}
              >
                Keep a look out for our Board Member Update emails!
              </Typography>
            </Box>
            {companyBoardOwner && (
              <div role="none" style={classes.ownerImageWrapper}>
                <img
                  alt=""
                  style={classes.dialogOwnerImage}
                  src={
                    companyBoardOwner?.image?.id &&
                    !companyBoardOwner.image.id.includes('undefined')
                      ? `${process.env.REACT_APP_IMAGES_URL}${companyBoardOwner.image.id}`
                      : DefaultAdvisorAvatar
                  }
                />
                <div style={classes.dialogBoardOwnerContainer}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: 'Poppins',
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#232B35',
                    }}
                  >
                    {companyBoardOwner.displayName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: 'Poppins',
                      fontSize: 13,
                      fontWeight: 400,
                      color: '#232B35',
                    }}
                  >
                    {`${getTextTrimmed(
                      companyBoardOwner.title,
                      20
                    )} - ${getTextTrimmed(companyBoardData.displayName, 40)}`}
                  </Typography>
                </div>
              </div>
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: !isSM ? '16px' : '32px',
                mb: '16px',
              }}
            >
              <Button
                id={`enterBoardroom${companyBoardData?.displayName.replace(
                  /\s+/g,
                  ''
                )}`}
                variant="contained"
                color="primary"
                onClick={() => setIsFirstViewModalVisible(false)}
                className="getStartedButton"
                style={{
                  backgroundColor: '#3578F4',
                  paddingTop: 15,
                  paddingBottom: 15,
                  textTransform: 'none',
                  fontFamily: 'Poppins',
                  fontWeight: 700,
                  fontSize: 15,
                  width: 180,
                  height: 44,
                  borderRadius: 30,
                }}
              >
                Enter Boardroom
              </Button>
            </Box>

            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: '#232B35',
                textAlign: 'center',
                marginBottom: '8px',
              }}
            >
              Perks unlocked:
            </Typography>
            {displayPills.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 1,
                  maxWidth: 'calc(100% + 20px)',
                  boxSizing: 'border-box',
                  mb: 1,
                  mx: '30px',
                }}
              >
                {displayPills.map((pill) => (
                  <MUIChip
                    key={pill.label}
                    size="small"
                    icon={<span style={{ fontSize: '12px' }}>{pill.icon}</span>}
                    label={pill.label}
                    sx={{
                      fontSize: '12px',
                      fontWeight: '500',
                      px: 0.8,
                      py: 0.4,
                      borderRadius: '1em',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      backgroundColor: '#f3f4f6',
                    }}
                    aria-label={pill.label}
                  />
                ))}
              </Box>
            )}
          </DialogContent>
        </div>
      </Dialog>
      <CalendarSyncMenu
        anchorEl={calendarAnchorEl}
        setAnchorEl={setCalendarAnchorEl}
        syncStates={{ googleSync, appleSync }}
        setSyncStates={{ setGoogleSync, setAppleSync }}
        advisorId={accountDetails?.id}
        companyIds={accountDetails?.boards?.memberBoards?.map(
          (board) => board.id
        )}
        setIsLoading={setIsLoading}
        showSnackbar={(message, severity) => {
          setSnackMessage(message);
          setSnackSeverity(severity);
          setSnackbarOpen(true);
        }}
      />
      <ACSnackbar
        style={{ marginTop: isFreemium ? '50px' : '0px' }}
        open={snackbarOpen}
        text={snackMessage}
        severity={snackSeverity}
        onClose={() => setSnackbarOpen(false)}
        autoHideDuration={5000}
      />
    </>
  );
}
